import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { getWalletBalance, getWalletTransactions } from '../services/authApi';
import {
  Wallet, Plus, ArrowUpRight, ArrowDownLeft, Clock, CreditCard,
  Coins, Gift, CheckCircle, XCircle, Loader2, IndianRupee, Sparkles,
  ArrowLeft, RefreshCw
} from 'lucide-react';
import apiClient from '../services/apiClient';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

export const WalletPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [activeTab, setActiveTab] = useState('recharge'); // 'recharge' or 'history'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceData, txData, pkgData] = await Promise.all([
        getWalletBalance(),
        getWalletTransactions(),
        apiClient.get('/wallet/packages')
      ]);

      setBalance(balanceData.balance || 0);
      setTransactions(txData.transactions || []);
      setPackages(pkgData.packages || []);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRecharge = async (coins, amount) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to recharge your wallet.",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    setProcessingPayment(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order
      const orderData = await apiClient.post(
        '/wallet/create-order',
        { amount, coins }
      );

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id || RAZORPAY_KEY,
        amount: amount * 100,
        currency: orderData.currency || 'INR',
        name: 'Avyudha',
        description: `Wallet Recharge - ${coins} Coins`,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyData = await apiClient.post(
              `/wallet/verify-payment?razorpay_order_id=${response.razorpay_order_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`
            );

            toast({
              title: "Payment Successful!",
              description: `${verifyData.coins_added} coins added to your wallet.`,
            });

            // Refresh data
            fetchData();
          } catch (error) {
            toast({
              title: "Verification Failed",
              description: "Payment was made but verification failed. Please contact support.",
              variant: "destructive",
            });
          }
          setProcessingPayment(false);
        },
        prefill: {
          name: orderData.user_name || user?.full_name || '',
          email: orderData.user_email || user?.email || '',
          contact: orderData.user_phone || ''
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
            toast({
              title: "Payment Cancelled",
              description: "You cancelled the payment.",
            });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setProcessingPayment(false);
        toast({
          title: "Payment Failed",
          description: response.error?.description || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      });
      razorpay.open();
    } catch (error) {
      setProcessingPayment(false);
      console.error('Payment error:', error);
      toast({
        title: "Payment Error",
        description: error.response?.data?.detail || "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCustomRecharge = () => {
    const amount = parseInt(customAmount);
    if (!amount || amount < 10) {
      toast({
        title: "Invalid Amount",
        description: "Minimum recharge amount is ₹10",
        variant: "destructive",
      });
      return;
    }
    // 1 rupee = 1 coin for custom amounts
    handleRecharge(amount, amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Wallet Balance Card */}
        <Card className="border-0 shadow-xl bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white mb-8 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
          <CardContent className="p-8 relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">Available Balance</p>
                <div className="flex items-center gap-3">
                  <Coins className="h-10 w-10 text-yellow-300" />
                  <span className="text-5xl font-bold">{balance}</span>
                  <span className="text-blue-200 text-xl mt-2">coins</span>
                </div>
                <p className="text-blue-200 text-sm mt-3">
                  {user?.email || 'User'}
                </p>
              </div>
              <Button
                onClick={fetchData}
                variant="ghost"
                className="text-white hover:bg-white/10"
                size="icon"
              >
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'recharge' ? 'default' : 'outline'}
            onClick={() => setActiveTab('recharge')}
            className={activeTab === 'recharge' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <Plus className="h-4 w-4 mr-2" />
            Recharge
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <Clock className="h-4 w-4 mr-2" />
            Transaction History
          </Button>
        </div>

        {/* Recharge Tab */}
        {activeTab === 'recharge' && (
          <div className="space-y-6">
            {/* Coin Packages */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Gift className="h-5 w-5 text-blue-600" />
                  Choose a Coin Package
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => { setSelectedPackage(pkg); setShowCustom(false); }}
                      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${selectedPackage?.id === pkg.id
                          ? 'border-blue-600 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-blue-300 bg-white'
                        }`}
                    >
                      {pkg.popular && (
                        <Badge className="absolute -top-2 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 text-xs">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Popular
                        </Badge>
                      )}
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-2">
                          <Coins className="h-6 w-6 text-yellow-500" />
                          <span className="text-3xl font-bold text-gray-900">{pkg.coins}</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-3">coins</p>
                        <div className="flex items-center justify-center gap-1">
                          <IndianRupee className="h-4 w-4 text-gray-700" />
                          <span className="text-2xl font-bold text-gray-900">{pkg.amount}</span>
                        </div>
                        {pkg.coins > pkg.amount && (
                          <p className="text-xs text-green-600 font-medium mt-1">
                            Save {Math.round((1 - pkg.amount / pkg.coins) * 100)}%
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Custom Amount */}
                  <div
                    onClick={() => { setSelectedPackage(null); setShowCustom(true); }}
                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${showCustom
                        ? 'border-blue-600 bg-blue-50 shadow-md'
                        : 'border-gray-200 border-dashed hover:border-blue-300 bg-white'
                      }`}
                  >
                    <div className="text-center">
                      <Plus className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-700">Custom Amount</p>
                      <p className="text-xs text-gray-500 mt-1">Enter your own amount</p>
                    </div>
                  </div>
                </div>

                {/* Custom Amount Input */}
                {showCustom && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          placeholder="Enter amount (min ₹10)"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          className="pl-9"
                          min="10"
                        />
                      </div>
                      <Button
                        onClick={handleCustomRecharge}
                        disabled={processingPayment || !customAmount}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {processingPayment ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pay Now'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">₹1 = 1 coin for custom amounts</p>
                  </div>
                )}

                {/* Pay Button for Selected Package */}
                {selectedPackage && (
                  <div className="mt-6">
                    <Button
                      onClick={() => handleRecharge(selectedPackage.coins, selectedPackage.amount)}
                      disabled={processingPayment}
                      className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg font-semibold shadow-lg"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-5 w-5 mr-2" />
                          Pay ₹{selectedPackage.amount} for {selectedPackage.coins} Coins
                        </>
                      )}
                    </Button>
                    <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500">
                      <img src="https://badges.razorpay.com/badge-dark.png" alt="Razorpay" className="h-6"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span>Secure payment powered by Razorpay</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Transaction History Tab */}
        {activeTab === 'history' && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Clock className="h-5 w-5 text-blue-600" />
                Transaction History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No transactions yet</p>
                  <p className="text-gray-400 text-sm mt-1">Your recharge and purchase history will appear here</p>
                  <Button
                    onClick={() => setActiveTab('recharge')}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Recharge Now
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, index) => (
                    <div
                      key={tx.id || index}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.transaction_type === 'credit'
                            ? 'bg-green-100'
                            : 'bg-red-100'
                          }`}>
                          {tx.transaction_type === 'credit'
                            ? <ArrowDownLeft className="h-5 w-5 text-green-600" />
                            : <ArrowUpRight className="h-5 w-5 text-red-600" />
                          }
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tx.description}</p>
                          <p className="text-sm text-gray-500">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${tx.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {tx.transaction_type === 'credit' ? '+' : '-'}{tx.amount} coins
                        </p>
                        <Badge variant="outline" className={`text-xs ${tx.status === 'completed'
                            ? 'text-green-600 border-green-200'
                            : tx.status === 'failed'
                              ? 'text-red-600 border-red-200'
                              : 'text-yellow-600 border-yellow-200'
                          }`}>
                          {tx.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {tx.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                          {tx.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
